import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import './Graph.css';
import React, { useEffect, useRef } from 'react';

const BirdCountGraph = React.memo((props: any) => {
  const yAxisDataRef = useRef<any[]>([]);

  useEffect(() => {
    if (props.yAxisData !== null) {
      yAxisDataRef.current = props.yAxisData;
      // Clean up any existing tooltips
      removeTooltip();
    }
  }, [props.yAxisData]);

  // Clean up tooltips on component unmount and add global listeners
  useEffect(() => {
    const handleScroll = () => removeTooltip();
    const handleClick = () => removeTooltip();
    const handleResize = () => removeTooltip();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);

    return () => {
      removeTooltip();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (props.yAxisData === null) {
    return (
      <div 
        style={{ 
          height: props.graphHeight, 
          width: props.graphWidth,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}
      >
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  function showTooltip(event: any, index: any) {
    const data = yAxisDataRef.current[index];
    if (!data) return;

    // Remove existing tooltip first
    removeTooltip();

    let parentElement: HTMLElement = document.getElementById(
      'combined-graph',
    ) as HTMLElement;
    if (!parentElement) return;

    // Get mouse position relative to the viewport
    const mouseX = event.clientX || event.pageX;
    const mouseY = event.clientY || event.pageY;
    
    let div = document.createElement('div');
    div.id = 'line';
    div.className = 'bar-custom-hover-line';
    div.style.position = 'fixed';
    div.style.pointerEvents = 'none';
    div.style.zIndex = '9999';
    div.style.left = mouseX + 10 + 'px';
    div.style.top = mouseY - 40 + 'px';
    div.style.width = 'auto';
    div.style.height = 'auto';
    div.style.transition = 'opacity 0.2s ease-in-out';
    div.style.opacity = '0';
    
    let innerDiv = document.createElement('div');
    innerDiv.className = 'bar-custom-tooltip';
    innerDiv.style.position = 'relative';
    innerDiv.style.background = 'rgba(0, 0, 0, 0.9)';
    innerDiv.style.color = 'white';
    innerDiv.style.padding = '8px 12px';
    innerDiv.style.borderRadius = '8px';
    innerDiv.style.fontSize = '12px';
    innerDiv.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
    innerDiv.style.minWidth = '150px';
    innerDiv.style.maxWidth = '250px';
    
    let content = '';
    if (data[0] < 25) {
      content = `
        <div>${data[0]}:00-${data[0]}:59</div>
        <div>Average number of birds: ${data[1]}</div>
      `;
    } else {
      const date: Date = new Date(data[0]);
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      content = `
        <div>${formattedDate}</div>
        <div>Number of birds: ${data[1]}</div>
      `;
    }
    
    const reliableText = props.type === 'day' ? 'Hours Recorded' : 'Reliable data';
    const value = typeof data[2] === 'number' ? data[2].toFixed(2) : data[2];
    content += `<div>${reliableText}: ${value}</div>`;
    
    innerDiv.innerHTML = content;
    div.appendChild(innerDiv);
    
    // Append to body instead of chart container
    document.body.appendChild(div);
    
    // Fade in the tooltip
    requestAnimationFrame(() => {
      div.style.opacity = '1';
    });
  }

  function removeTooltip() {
    // Remove all existing tooltips to prevent multiple tooltips
    const existingTooltips = document.querySelectorAll('#line, .bar-custom-hover-line');
    existingTooltips.forEach(tooltip => {
      if (tooltip instanceof HTMLElement) {
        tooltip.style.opacity = '0';
        setTimeout(() => {
          if (tooltip && tooltip.parentNode) {
            tooltip.remove();
          }
        }, 200);
      }
    });
  }
  const options: ApexOptions = {
    colors: props.colors ? props.colors : ['#8B8296'],
    fill: {
      opacity: 1,
    },
    plotOptions: {
      bar: {
        columnWidth: '100%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    markers: {
      size: 1,
    },
    tooltip: {
      enabled: false,
    },
    stroke: { width: 0 },

    xaxis: props.xAxisOptions,
    yaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
      tickAmount: props.tickAmount,
    },
    grid: {
      show: props.showGrid,
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        filter: {
          type: 'none',
        },
      },
    },
    chart: {
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 300,
        dynamicAnimation: {
          enabled: false,
        },
      },
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      events: {
        dataPointMouseEnter: (event: any, chartContext: any, config: any) => {
          if (props.eventsEnabled) {
            showTooltip(event, config.dataPointIndex);
          }
        },
        dataPointMouseLeave: (event: any, chartContext: any, config: any) => {
          if (props.eventsEnabled) {
            removeTooltip();
          }
        },
        mouseLeave: (event: any, chartContext: any, config: any) => {
          if (props.eventsEnabled) {
            removeTooltip();
          }
        },
      },
    },
  };
  const series = [
    {
      data: props.yAxisData,
    },
  ];

  return (
    <div
      id="chart"
      className={props.className}
      onMouseLeave={removeTooltip}
      onMouseEnter={removeTooltip}
      style={{ width: '100%', overflow: 'hidden' }}
    >
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={props.graphHeight}
        width="100%"
      />
    </div>
  );
});

export default BirdCountGraph;
